import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { IconButton } from './IconButton';

/**
 * A criterion in the active-filter bar (`FE-04.10`;
 * `docs/design/stage2/finding-things.md`'s `FilterChip` row, `FE-32.1`,
 * `FR-SEARCH-009`).
 *
 * **Theme-coloured, and it never takes a palette colour.** Considered as a
 * `LabelChip` variant and rejected on colour, by both `finding-things.md` and
 * `B3-BRIEF.md` §9: `LabelChip`'s fill is board *data* from the thirty-colour
 * label palette, and a chip reading "Member: Priya" or "Due: overdue" has no
 * such colour — a header of `LabelChip`s would acquire a random palette drawn
 * from data that does not apply. So this is `--color-surface-raised` +
 * `--color-border-strong`, full stop.
 *
 * `value` is a **slot**, not a colour: a chip representing a label renders a
 * `LabelChip` inside it (another agent's component), and this component never
 * imports or guesses at that palette.
 *
 * Carries its own remove `IconButton` — a real, separately focusable control,
 * not a click-anywhere-on-the-chip affordance that would make "view the
 * filter" and "remove the filter" the same gesture.
 */

const filterChipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-surface-raised py-1 pl-3 pr-1 text-sm text-ink',
);

export interface FilterChipProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'children' | 'onRemove'
> {
  /** e.g. "Member", "Due", "Label" — rendered as the chip's muted prefix. */
  facet: string;
  /** The criterion's value. A plain string, or another primitive's slot. */
  value: ReactNode;
  onRemove: () => void;
  /** @default `primitives.filterChip.remove` with the facet as a slot. */
  removeLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

export function FilterChip({
  facet,
  value,
  onRemove,
  removeLabel,
  className,
  ref,
  ...rest
}: FilterChipProps) {
  const { t } = useInterfaceLanguage();
  const resolvedRemoveLabel = removeLabel ?? t('primitives.filterChip.remove', { facet });

  return (
    <div ref={ref} className={cn(filterChipVariants(), className)} {...rest}>
      <span>
        {/* Opaque chip, so muted text here clears the panel's 92% floor with
         * room to spare — this is `--color-surface-raised`, never `--color-panel`. */}
        <span className="text-ink-muted">{facet}: </span>
        {value}
      </span>
      <IconButton
        size="sm"
        variant="quiet"
        label={resolvedRemoveLabel}
        onClick={onRemove}
      >
        <RemoveGlyph />
      </IconButton>
    </div>
  );
}

function RemoveGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden="true" focusable="false">
      <path
        d="M4 4l8 8M12 4l-8 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}
